import { ApolloError } from 'apollo-client'
import { UserErrorFragment } from 'gqlTypes/operations'
import { ErrorHandler } from '../../../modules/tracing/ErrorHandler'

// We know that we'll want to switch to a different, other than Apollo GraphQL client library.
// Therefore we are already hiding access to the originalError.
// if anything more tailored would be needed it would be best to expand this interface
// with a generic function/property
// or as a last resort cast ParsedError to ApolloParsedError

export interface ParsedError {
  // parsed and concatenated error string
  /**
   * Parsed and concatenated error string.
   * We are using both `list of GraphQL errors` and `Network error`
   * to build this error message.
   */
  readonly message: string

  /**
   * Returns of all error messages returned.
   * This list includes both GraphQL errors and Network error.
   */
  readonly messages: string[]

  /**
   * Returns of all `GraphQL` error messages returned.
   */
  readonly graphqlErrorMessages: string[]

  /**
   * Returns the message of Network error if network error exists,
   * or `undefined` if network error non existing
   */
  readonly networkErrorMessage: string | undefined

  /**
   * check whether any of the returned errors includes an error code
   */
  includesErrorCode(value: string): boolean
}

export class ApolloParsedError extends Error implements ParsedError {
  constructor(public readonly originalError: ApolloError) {
    super()
  }

  get message(): string {
    const error = this.originalError
    const parsedMessage =
      error.graphQLErrors?.map(x => x.message).join(', ') ||
      error.networkError?.message ||
      error.message

    if (parsedMessage) {
      return parsedMessage
    }

    ErrorHandler.shouldNeverHappen(
      'ApolloParsedError should always parse a truthy error message '
    )

    return 'Something is wrong, please contact our support team'
  }

  get messages() {
    const { graphqlErrorMessages, networkErrorMessage } = this

    if (networkErrorMessage) {
      return [...graphqlErrorMessages, networkErrorMessage]
    }

    return graphqlErrorMessages
  }

  get graphqlErrorMessages() {
    return this.originalError.graphQLErrors?.map(x => x.message)
  }

  get networkErrorMessage() {
    return this.originalError.networkError?.message || undefined
  }

  includesErrorCode(value: string) {
    const error = this.originalError

    return error.graphQLErrors.some(e => e.extensions?.code === value)
  }

  /**
   * @deprecated
   * This field is only for backwards compatibility (particularly for .js files)
   * and it is meant only for the transition.
   */
  get graphQLErrors() {
    return this.originalError.graphQLErrors
  }

  /**
   * @deprecated
   * This field is only for backwards compatibility (particularly for .js files)
   * and it is meant only for the transition.
   */
  get networkError() {
    return this.originalError.networkError
  }

  /**
   * @deprecated
   * This field is only for backwards compatibility (particularly for .js files)
   * and it is meant only for the transition.
   */
  get extraInfo() {
    return this.originalError.extraInfo
  }

  /**
   * @deprecated
   * This field is only for backwards compatibility (particularly for .js files)
   * and it is meant only for the transition.
   */
  get name() {
    return this.originalError.name
  }
}

export class UserParsedError extends Error implements ParsedError {
  constructor(public readonly originalErrors: UserErrorFragment[]) {
    super()
  }

  get message(): string {
    return this.messages.join(', ')
  }

  get messages() {
    return this.originalErrors.map(x => x.message)
  }

  get graphqlErrorMessages() {
    return this.messages
  }

  get networkErrorMessage() {
    return undefined
  }

  includesErrorCode(value: string) {
    return this.originalErrors.some(x => x.code === value)
  }
}

export class RawParsedError extends Error implements ParsedError {
  constructor(public readonly originalError: Error) {
    super()
  }

  get message(): string {
    return this.originalError.message
  }

  get messages() {
    return [this.message]
  }

  get graphqlErrorMessages() {
    return []
  }

  get networkErrorMessage() {
    return this.originalError.message
  }

  includesErrorCode(value: string) {
    return false
  }
}
